import { Organization } from "@/model/Organization"
import {
  CreatePregnancyApiDto,
  Pregnancy,
  SupportalPregnancyApiDto,
  toPregnancy,
} from "@/model/Pregnancy"
import {
  CreateDiabetesPatientPrescriptionDto,
  CreateHypertensionPatientPrescriptionDto,
  Prescription,
  PrescriptionApiDto,
  toPrescription,
} from "@/model/Prescription"
import { Instant, LocalDate, LocalTime } from "@js-joda/core"
import { Device } from "./Devices"
import { ReminderDayTime, ReminderDayTimeApiDto } from "./Reminders"

export type LastUpdatedBy = "patient" | "manufacturer" | "emr"

export type SupportalCustomerSupportNotes = {
  reason: string
  notes: string
  flagged: boolean
}

export type Patient = {
  id: string
  bloomlifeId: string
  medicalRecordNumber: string
  dateOfBirth?: LocalDate
  firstName: string
  lastName: string
  phoneNumber?: string
  emailAddress?: string
  preferredLocale?: string
  timeZone: string
  loginState: LoginState
  insuranceProvider?: string
  shippingAddress?: UsPostalAddress
  shippingAddressLastUpdatedBy?: string
  activeHypertensionPrescriptionId?: string
  activeDiabetesPrescriptionId?: string
  hasBloodPressureMonitor: boolean
  hasBloodGlucoseMonitor: boolean
  customerSupportNotes?: SupportalCustomerSupportNotes
  isAttCompatible?: boolean
}

export type ImportedPatient = {
  medicalRecordNumber: string
  dateOfBirth?: LocalDate
  firstName?: string
  lastName?: string
  phoneNumber?: string
  emailAddress?: string
  preferredLocale?: string
  timeZone?: string
  shippingAddress?: UsPostalAddress
}

export type UsPostalAddress = {
  address1?: string
  address2?: string
  city?: string
  zipCode?: string
  state?: string
}

export type UsPostalAddressApiDto = {
  address1?: string
  address2?: string
  city?: string
  zipCode?: string
  state?: string
}

export type LoginState = "no-access" | "invited" | "active"

export type PatientApiDto = {
  id: string
  bloomlifeId: string
  medicalRecordNumber: string
  firstName: string
  lastName: string
  emailAddress?: string
  phoneNumber?: string
  dateOfBirth?: string
  preferredLocale?: string
  timeZone: string
  loginState: LoginState
  insuranceProvider?: string
  shippingAddress?: UsPostalAddress
  shippingAddressLastUpdatedBy?: LastUpdatedBy
  activeHypertensionPrescriptionId?: string
  activeDiabetesPrescriptionId?: string
  hasBloodPressureMonitor: boolean
  hasBloodGlucoseMonitor: boolean
  customerSupportNotes?: SupportalCustomerSupportNotes
}

export type ImportedPatientApiDto = {
  medicalRecordNumber: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  phoneNumber?: string
  dateOfBirth?: string
  preferredLocale?: string
  timeZone?: string
  shippingAddress?: UsPostalAddress
}

export type PatientDetailPageDataApiDto = {
  patient: PatientApiDto
  organization: Organization
  prescriptions: PrescriptionApiDto[]
  pregnancies: SupportalPregnancyApiDto[]
  devices: Device[]
  reminders: ReminderDayTimeApiDto[]
}

export type PatientDetailPageData = {
  patient: Patient
  organization: Organization
  prescriptions: Prescription[]
  pregnancies: Pregnancy[]
  devices: Device[]
  reminders: ReminderDayTime[]
}

const labelForLastUpdatedBy = (
  lastUpdatedBy?: Patient["shippingAddressLastUpdatedBy"]
): string => {
  switch (lastUpdatedBy) {
    case "patient":
      return "patient"
    case "manufacturer":
      return "support"
    case "emr":
      return "EMR"
    default:
      return lastUpdatedBy || "Unknown"
  }
}

export const toImportedPatient = (
  dto: ImportedPatientApiDto
): ImportedPatient => ({
  ...dto,
  dateOfBirth: dto.dateOfBirth ? LocalDate.parse(dto.dateOfBirth) : undefined,
})

export const toPatient = (dto: PatientApiDto): Patient => ({
  ...dto,
  dateOfBirth: dto.dateOfBirth ? LocalDate.parse(dto.dateOfBirth) : undefined,
  activeDiabetesPrescriptionId: dto.activeDiabetesPrescriptionId || undefined,
  activeHypertensionPrescriptionId:
    dto.activeHypertensionPrescriptionId || undefined,
  shippingAddressLastUpdatedBy: labelForLastUpdatedBy(
    dto.shippingAddressLastUpdatedBy
  ),
})

export const toReminderDayTime = (
  reminder: ReminderDayTimeApiDto
): ReminderDayTime => ({
  ...reminder,
  time: LocalTime.parse(reminder.time),
})

export const toPatientDetailPageData = (
  dto: PatientDetailPageDataApiDto
): PatientDetailPageData => ({
  patient: toPatient(dto.patient),
  organization: dto.organization,
  prescriptions: dto.prescriptions.map(toPrescription),
  pregnancies: (dto.pregnancies ?? []).map(toPregnancy),
  devices: dto.devices,
  reminders: dto.reminders
    .filter(({ type }) => type === "measurement")
    .map(toReminderDayTime),
})

export type AddPatientDto = {
  medicalRecordNumber: string
  bloomlifeId: string
  firstName: string
  lastName: string
  dateOfBirth?: string
  preferredLocale?: string
  phoneNumber: string
  emailAddress?: string
  insuranceProvider?: string
  shippingAddress?: {
    address1?: string
    address2?: string
    city?: string
    state?: string
    zipCode?: string
  }
  pregnancy: CreatePregnancyApiDto
  hypertensionPrescription?: CreateHypertensionPatientPrescriptionDto
  diabetesPrescription?: CreateDiabetesPatientPrescriptionDto
  timeZone?: string
  isAttCompatible?: boolean
}

export type BloodGlucoseFormContextApiDto = {
  type:
    | "fasting"
    | "snack-before-breakfast"
    | "breakfast"
    | "snack-between-breakfast-and-lunch"
    | "lunch"
    | "snack-between-lunch-and-dinner"
    | "dinner"
    | "snack-after-dinner"
    | "quick-check"
}

export type ReadingApiDto = {
  deviceId: string
  dateTime: string
  bloodPressure: string | null
  bloodGlucoseInMilligramsPerDeciliter: number | null
  classification: string
  formContext: BloodGlucoseFormContextApiDto | null
  emrPushStatus?: "complete" | "pending" | "failed"
  syncedWithBioT?: boolean
  smsSent?: boolean
}

export type NotificationLogEntryType =
  | "LOW_BLOOD_PRESSURE"
  | "ELEVATED_BLOOD_PRESSURE"
  | "CRITICAL_BLOOD_PRESSURE"
  | "HYPERTENSION_REVIEW"
  | "HYPERTENSION_TIGHTENED_CONTROL"
  | "WEEKLY_HYPERTENSION_REPORT"
  | "EMERGENCY_LOW_BLOOD_GLUCOSE"
  | "CRITICAL_LOW_BLOOD_GLUCOSE"
  | "DIABETES_REVIEW"
  | "DIABETES_TIGHTENED_CONTROL"
  | "WEEKLY_DIABETES_REPORT"

export type NotificationLogEntryStatus =
  | "PENDING"
  | "SENDING"
  | "SENT"
  | "SKIPPED"
  | "DISABLED"
  | "SEND_FAILED"
  | "INACTIVE_PRESCRIPTION"

export type EmrNotificationApiDto = {
  id: string
  type: NotificationLogEntryType
  status: NotificationLogEntryStatus
  enqueuedAt: string
  sentAt?: string
}

export type PrescriptionReportApiDto = {
  prescription: PrescriptionApiDto
  readings: ReadingApiDto[]
  emrNotifications: EmrNotificationApiDto[]
}

export type PrescriptionReport = {
  prescription: Prescription
  firstMeasurementTime: Instant | undefined
  totalMeasurements: number
  totalContextualizations: number
  readings: ReadingApiDto[]
  emrNotifications: EmrNotificationApiDto[]
}

export const toPrescriptionReport = (
  dto: PrescriptionReportApiDto
): PrescriptionReport => {
  const prescription = toPrescription(dto.prescription)

  // Only diabetes
  const totalContextualizations =
    prescription.type === "diabetes"
      ? dto.readings.filter((r) => r.formContext !== null).length
      : 0

  return {
    prescription,
    firstMeasurementTime: prescription.firstReadingTime,
    totalMeasurements: dto.readings.length,
    totalContextualizations,
    readings: dto.readings,
    emrNotifications: dto.emrNotifications,
  }
}
